import { BIOMES, type BiomeBand, type BiomeId } from './biome.js';
import { generateHeightfield, heightAt, slopeAt, type Heightfield } from './heightfield.js';
import {
  itemCountFor,
  placeItems,
  ITEM_CLEAR_OF_PROPS_M,
  ITEM_SPAN_MIN_M,
  type ItemSpec,
} from './items.js';
import { between, deriveSeed, mulberry32, oneOf } from './rand.js';
import type { RouteDef } from './route.js';
import { weatherFor } from './weather.js';
import {
  propLengthFor,
  PROP_SIZES,
  propFootprint,
  type DecorKind,
  type DecorSpec,
  type PropKind,
  type PropSpec,
} from './props.js';
import type { SurfaceId, SurfaceSegment } from './surfaces.js';

/**
 * Making a route.
 *
 * Two hand-authored routes, alternating, meant she had seen everywhere the game had by the fifth session.
 * Every route is generated now - the first one included.
 *
 * That trade has to be paid for. Two routes were safe because they had been *played*; three hundred
 * thousand possible routes cannot be. So the safety properties that used to be true by inspection are
 * enforced here, by construction, and asserted over hundreds of seeds in `tests/route-gen.test.ts`:
 *
 *  - the slope clamp still runs over every generated heightfield, so no hill can defeat her;
 *  - the spawn and the goal always sit on flat, plain ground;
 *  - **ice is only ever laid on the level**, because ice on a climb is the one surface combination that can
 *    genuinely strand her;
 *  - nothing is placed where it would hide the item she is driving towards;
 *  - a jump's face is never steeper than a vehicle's breakover angle.
 *
 * Pure and seeded: same seed, same route, forever. That is what lets the return leg regenerate the world
 * rather than remember it, and what lets a route she liked be found again from one number.
 */

export interface RouteRecipe {
  readonly id: string;
  /** Parent-facing. Nothing she sees has words on it. */
  readonly label: string;
  /** Which places this kind of drive passes through. Two or three are chosen per route. */
  readonly biomes: readonly BiomeId[];
  readonly lengthMinM: number;
  readonly lengthMaxM: number;
  /** Peak-to-trough hill height at difficulty 0 and at difficulty 1. */
  readonly hillMinM: number;
  readonly hillMaxM: number;
  /** Roughest the fine detail gets, at difficulty 1. */
  readonly roughnessMax: number;
  /** How many patches of something-other-than-default to lay, at difficulty 0 and 1. */
  readonly patchesMin: number;
  readonly patchesMax: number;
  /** Built ramps to jump. Zero on the gentlest recipes. */
  readonly jumpsMax: number;
  /** Mounds to bump over - logs, rocks, bales, speed bumps - at difficulty 0 and at difficulty 1. */
  readonly moundsMin: number;
  readonly moundsMax: number;
  /** Which unlock this recipe waits for, or null if it is always available. */
  readonly requiredUnlock: string | null;
}

/**
 * The kinds of drive.
 *
 * A recipe is not a route: it is what sort of place this is and how wild it may get. The seed decides
 * everything else, so one recipe is a thousand drives rather than one.
 */
export const RECIPES: readonly RouteRecipe[] = [
  {
    id: 'meadow-lane',
    label: 'green lanes',
    biomes: ['meadow', 'forest'],
    lengthMinM: 52,
    lengthMaxM: 72,
    hillMinM: 0.9,
    hillMaxM: 1.7,
    roughnessMax: 0.35,
    patchesMin: 1,
    patchesMax: 3,
    jumpsMax: 1,
        moundsMin: 1,
    moundsMax: 3,
    requiredUnlock: null,
  },
  {
    id: 'seaside-run',
    label: 'down to the sea',
    biomes: ['meadow', 'beach'],
    lengthMinM: 56,
    lengthMaxM: 78,
    hillMinM: 0.8,
    hillMaxM: 1.6,
    roughnessMax: 0.3,
    patchesMin: 2,
    patchesMax: 4,
    jumpsMax: 1,
        moundsMin: 1,
    moundsMax: 3,
    requiredUnlock: null,
  },
  {
    id: 'town-errand',
    label: 'through the town',
    biomes: ['town', 'meadow'],
    lengthMinM: 52,
    lengthMaxM: 70,
    hillMinM: 0.6,
    hillMaxM: 1.2,
    roughnessMax: 0.22,
    patchesMin: 1,
    patchesMax: 2,
    jumpsMax: 1,
        moundsMin: 2,
    moundsMax: 4,
    requiredUnlock: null,
  },
  /*
   * The three below are the wilder ground: bigger hills, rougher going, more to drive over. They waited on
   * `route-hills` until the prize table became hats only, and nothing gates them now.
   *
   * That is the right way round. Where she drives is decided by `difficultyFor` - the ground gets gently
   * more interesting as she finishes missions - and holding half the recipes back behind a prize meant the
   * first nine missions were drawn from three recipes rather than six, which is the same drive over and
   * over at exactly the age when novelty is the whole appeal. `requiredUnlock` stays on the shape because
   * `MissionDef` carries it and the director filters on it, so a future gate needs no new plumbing.
   */
  {
    id: 'hill-country',
    label: 'the big hills',
    biomes: ['meadow', 'forest', 'canyon'],
    lengthMinM: 64,
    lengthMaxM: 90,
    hillMinM: 1.5,
    hillMaxM: 2.4,
    roughnessMax: 0.5,
    patchesMin: 2,
    patchesMax: 4,
    jumpsMax: 2,
        moundsMin: 2,
    moundsMax: 5,
    requiredUnlock: null,
  },
  {
    id: 'winter-way',
    label: 'the snowy way',
    biomes: ['snow', 'forest'],
    lengthMinM: 58,
    lengthMaxM: 80,
    hillMinM: 1,
    hillMaxM: 1.9,
    roughnessMax: 0.35,
    patchesMin: 2,
    patchesMax: 4,
    jumpsMax: 1,
        moundsMin: 1,
    moundsMax: 4,
    requiredUnlock: null,
  },
  {
    id: 'red-canyon',
    label: 'the red rocks',
    biomes: ['canyon', 'beach'],
    lengthMinM: 60,
    lengthMaxM: 84,
    hillMinM: 1.2,
    hillMaxM: 2.1,
    roughnessMax: 0.45,
    patchesMin: 2,
    patchesMax: 3,
    jumpsMax: 2,
        moundsMin: 2,
    moundsMax: 5,
    requiredUnlock: null,
  },
];

export function recipeById(id: string): RouteRecipe {
  return RECIPES.find((recipe) => recipe.id === id) ?? RECIPES[0]!;
}

/** Which recipes she can be sent on, given what she has unlocked. */
export function availableRecipes(unlocks: readonly string[]): readonly RouteRecipe[] {
  const open = RECIPES.filter(
    (recipe) => recipe.requiredUnlock === null || unlocks.includes(recipe.requiredUnlock),
  );
  // Never empty: an unlock table typo must not leave her with nowhere to drive.
  return open.length > 0 ? open : [RECIPES[0]!];
}

/**
 * Which kind of place a drive happens in when no mission chose one.
 *
 * Free play had no mission, so it fell through to a hardcoded `meadow-lane` - which made the one button on
 * the Hub that promises *somewhere new* the one drive guaranteed to be the same green lane every time. It is
 * also the button she presses most, so most of the game's worlds were never reachable at all.
 *
 * Derived from the seed rather than rolled fresh, because the seed is the only thing that survives a remount
 * of the drive screen: rolling here would change the place under her if the screen ever rebuilt itself, and
 * "the same seed is the same world" is the property everything else in this file depends on.
 */
export function recipeForSeed(seed: number, unlocks: readonly string[]): RouteRecipe {
  const open = availableRecipes(unlocks);
  return oneOf(mulberry32(deriveSeed(seed, 'recipe')), open) ?? open[0]!;
}

/**
 * How hard the world is allowed to be, from how many missions she has finished.
 *
 * 0 to 1, easing over about fifteen missions and then stopping. This is the whole progression axis now that
 * "the hilly route" is a recipe rather than a place: the ground gets gently more interesting as she gets
 * better at driving, and it is impossible for it to keep getting harder forever.
 *
 * Deliberately slow. At three, "the same but a bit more" is the shape of getting better; a difficulty curve
 * she can feel from one mission to the next reads as the game changing its mind.
 */
export function difficultyFor(missionsCompleted: number): number {
  const over = 15;
  // Explicitly, because Math.min/max propagate NaN rather than clamping it - and a NaN difficulty would
  // silently produce a route of NaN metres.
  if (!Number.isFinite(missionsCompleted)) return 0;
  const t = Math.max(0, Math.min(1, missionsCompleted / over));
  // Smoothstep, so the first few missions barely move and the middle does most of the work.
  return t * t * (3 - 2 * t);
}

/** Flat, plain ground either side of the Lookout and the goal. Matches the hand-authored routes. */
const MARGIN_M = 6;
/** Nothing is laid within this distance of the spawn or the goal. */
const KEEP_CLEAR_M = 4.5;
/** A ramp needs ground at least this level under it, for its whole length. */
const RAMP_MAX_GROUND_SLOPE = 0.08;
/** Ice may only be laid where the ground is at least this flat, over the whole patch. */
const ICE_MAX_SLOPE = 0.08;
/** How far apart two patches must be, so the ground does not become a stripe of everything. */
const PATCH_GAP_M = 3;
/** Shortest a biome band may be. Below this it cannot hold its own against the blends either side. */
const MIN_BAND_M = 16;
/**
 * Longest a mission route may be, however many things are on it.
 *
 * 130 metres is about thirty-eight seconds at the pace she drives, and about fifty for the slowest
 * vehicle on the hilliest recipe with a trailer behind it. Both sit inside the ninety-second budget a
 * phase gets, with room for the pickups themselves.
 */
const ROUTE_MAX_M = 130;

export interface GenerateOptions {
  readonly recipe: RouteRecipe;
  readonly seed: number;
  /** 0 to 1, from `difficultyFor`. */
  readonly difficulty: number;
  /** Override the length - free play wants a long one. */
  readonly lengthM?: number;
}

export function generateRoute(options: GenerateOptions): RouteDef {
  const { recipe, seed } = options;
  const difficulty = Math.max(0, Math.min(1, options.difficulty));
  const rand = mulberry32(deriveSeed(seed, 'route'));

  /*
   * A count and a length, decided together.
   *
   * The recipe still says how long the drive is whenever it is already long enough for what is on it;
   * the count only ever *stretches* it, never shrinks it, and never past `ROUTE_MAX_M`. A longer route
   * is only worth spending once there is something in the middle of it - the old 90-metre ceiling with
   * one item at the end just made the empty part longer.
   *
   * Free play overrides the length and gets a single item it never has to fetch, exactly as before.
   */
  const itemCount = options.lengthM === undefined ? itemCountFor(seed, difficulty) : 1;
  const lengthM =
    options.lengthM ??
    Math.min(
      ROUTE_MAX_M,
      Math.max(
        // Left inside the `??` rather than hoisted, so free play draws exactly as many numbers from
        // `rand` as it always did and its world does not shift under a change to mission lengths.
        Math.round(lerp(recipe.lengthMinM, recipe.lengthMaxM, difficulty * 0.6 + rand() * 0.4)),
        ITEM_SPAN_MIN_M * itemCount,
      ),
    );

  /*
   * Hills. Amplitude grows with difficulty; wavelength shrinks a little, which is what turns "rolling" into
   * "lumpy" without ever approaching the slope clamp - the clamp would flatten it anyway, and a route that
   * has been flattened by the clamp is a route whose shape was a lie.
   */
  const hillAmplitudeM = lerp(recipe.hillMinM, recipe.hillMaxM, difficulty * 0.75 + rand() * 0.25);
  const hillWavelengthM = between(rand, 15, 24) - difficulty * 3;
  const roughness = lerp(0.18, recipe.roughnessMax, difficulty * 0.8 + rand() * 0.2);

  const biomes = pickBands(recipe, rand, lengthM, difficulty);
  /*
   * How long a transition takes, decided *after* the bands and clamped by the shortest of them.
   *
   * The other way round, a three-band route on a short course produced a 15-metre band with an 11-metre
   * blend at each end - so the middle biome arrived and left at the same moment and never appeared as
   * itself. A band has to be at least a couple of transitions long to be a place at all.
   */
  const shortestBand = Math.min(...biomes.map((band) => band.toM - band.fromM));
  const blendM = Math.max(5, Math.min(Math.round(between(rand, 7, 11)), Math.floor(shortestBand / 2.6)));



  /*
   * The heightfield is built *before* the patches are placed, because patches need to know the shape of the
   * ground: a puddle belongs in a dip, and ice belongs on the level. Height depends only on the hills, the
   * ramps and the jumps, so there is no circularity - and the clamp inside `generateHeightfield` has
   * already run by the time anything is laid on it.
   */
  /*
   * Patches are laid before props, and the order is a judgement about what matters.
   *
   * Driving through mud and then through the stream that washes it off is the best cause-and-effect pair in
   * the game; a log in the road is variety. With props placed first they took the middle of the route and the
   * pairing dropped from nine times in ten to one in ten - measured. So the ground she drives *on* gets first
   * refusal, and the things standing on it fit around what is left.
   */
  const base: RouteDef = {
    id: `${recipe.id}-${seed >>> 0}`,
    seed,
    lengthM,
    hillAmplitudeM,
    hillWavelengthM,
    roughness,
    flatMarginM: MARGIN_M,
    ramps: [],
    surfaces: [],
    props: [],
    decor: [],
    items: [],
    defaultSurface: BIOMES[biomes[0]!.biome].defaultSurface,
    biomes,
    blendM,
    /*
     * The sky, from its own stream.
     *
     * `deriveSeed(seed, 'weather')` inside `weatherFor` rather than a draw from `rand` here, and that is not
     * fussiness: one extra `rand()` call at this point would shift every number after it, so adding weather
     * would have silently rearranged the hills, ramps and items of every route anybody had ever seen. Same
     * reason the scatter, the props and the spray each have their own stream.
     */
    weather: weatherFor({ seed, bands: biomes }),
  };

  const field = generateHeightfield(base);
  const surfaces = placePatches({
    recipe,
    rand,
    difficulty,
    lengthM,
    bands: biomes,
    blendM,
    field,
  });
  /*
   * The load goes down before the props, by the same judgement that put the patches before them.
   *
   * The load is the only thing on a route whose *absence* breaks the drive rather than thinning it: one
   * item short and the trailer never fills and the road home never opens. Props the other way round -
   * a route with one fewer log is a route. So the items get first refusal on the middle of the road and
   * the logs and ramps fit around what is left. Measured with it the other way: a four-item meadow
   * route came back with three, because the props had taken every flat spot the items needed.
   *
   * It draws from its own seeded stream, so the patches above are untouched by this existing at all.
   */
  const items = placeItems({ seed, count: itemCount, lengthM, field, surfaces });
  const props = placeProps(recipe, rand, difficulty, lengthM, surfaces, field, items);
  const decor = placeDecor(rand, difficulty, lengthM, props, items);

  return { ...base, surfaces, props, decor, items };
}

/**
 * Two or three biome bands across the route.
 *
 * Always more than one: a drive should go *somewhere*, and a single band is the flat, placeless world this
 * whole exercise exists to replace. Never so short that a band cannot be seen at full strength - a band
 * under two blend zones long arrives and leaves at the same moment, which looks like a colour glitch.
 */
function pickBands(
  recipe: RouteRecipe,
  rand: () => number,
  lengthM: number,
  difficulty: number,
): readonly BiomeBand[] {
  const pool = [...recipe.biomes];
  /*
   * Two bands, or three when every one of them can still be at least MIN_BAND_M long. A band shorter than
   * that cannot hold its own character against the transitions either side of it.
   */
  const usableSpan = lengthM * 0.72;
  const roomForThree = usableSpan / 3 >= MIN_BAND_M;
  const count =
    pool.length >= 3 && roomForThree && rand() < 0.35 + difficulty * 0.3 ? 3 : 2;

  const chosen: BiomeId[] = [];
  // Shuffled by picking without replacement, so the same recipe does not always start in the same place.
  for (let i = 0; i < count && pool.length > 0; i++) {
    const index = Math.min(pool.length - 1, Math.floor(rand() * pool.length));
    chosen.push(pool[index]!);
    pool.splice(index, 1);
  }
  while (chosen.length < count) chosen.push(chosen[0]!);

  /*
   * Borders are jittered but kept away from the ends: the first and last stretch is where she spawns and
   * where the goal sits, and a transition happening exactly there would make both harder to read.
   */
  const usable = usableSpan;
  const offset = lengthM * 0.14;
  const bands: BiomeBand[] = [];
  let from = -MARGIN_M - 14;

  for (let i = 0; i < chosen.length; i++) {
    const last = i === chosen.length - 1;
    const border = offset + (usable * (i + 1)) / chosen.length + between(rand, -3, 3);
    // The last band runs past the end of the ground, so the far margin is never uncoloured.
    const to = last ? lengthM + MARGIN_M + 14 : border;
    bands.push({ fromM: from, toM: to, biome: chosen[i]! });
    from = to;
  }

  return bands;
}

/**
 * Everything in the road with collision: ramps to launch off, mounds to bump over.
 *
 * Laid out with a running cursor rather than into fixed slots. Slots looked tidier and were wrong: when a slot
 * was narrower than the prop plus the clear ground it needs, the prop simply spilled past the end of it and
 * into its neighbour. A cursor cannot overlap anything by construction - it only ever moves forwards, past the
 * whole footprint of what it just placed.
 *
 * Anything that will not fit in the room left over is dropped rather than squeezed in. A route with one fewer
 * log is a route; a route with a log inside a hay bale is a bug.
 */
function placeProps(
  recipe: RouteRecipe,
  rand: () => number,
  difficulty: number,
  lengthM: number,
  patches: readonly SurfaceSegment[],
  field: Heightfield,
  items: readonly ItemSpec[],
): readonly PropSpec[] {
  const props: PropSpec[] = [];

  const jumpCount = Math.min(recipe.jumpsMax, Math.floor(difficulty * (recipe.jumpsMax + 0.8)));
  const moundCount = Math.round(lerp(recipe.moundsMin, recipe.moundsMax, difficulty));

  // Ramps first: they need six metres of run-off either side, against a mound's one and a half.
  const kinds: PropKind[] = [];
  for (let i = 0; i < jumpCount; i++) kinds.push('ramp');
  const moundKinds: PropKind[] = ['log', 'rock', 'bale', 'bump'];
  for (let i = 0; i < moundCount; i++) {
    kinds.push(moundKinds[Math.min(moundKinds.length - 1, Math.floor(rand() * moundKinds.length))]!);
  }
  if (kinds.length === 0) return props;

  const usableTo = lengthM - KEEP_CLEAR_M - 3;
  let cursor = KEEP_CLEAR_M + 3;

  for (const kind of kinds) {
    const size = PROP_SIZES[kind];
    const heightM = between(rand, size.minHeightM, size.maxHeightM);
    /*
     * A ramp carries its own length - longer than any vehicle's wheelbase, or the car straddles it and never
     * commits to the slope (measured in M4: 1.6m gave one frame of air, 3.6m gives a real hop). A mound's
     * length is *derived* from its height, and that ratio is the whole reason its faces stay climbable.
     */
    const lengthOfProp = kind === 'ramp' ? between(rand, 3.4, 4.2) : propLengthFor(kind, heightM);
    /*
     * A ramp reserves six metres *before* its foot, so starting the cursor at the same place for every kind
     * pushed the first ramp's run-off back past the start of the route. Each prop is nudged forward by
     * however much of its own footprint would otherwise hang off the front.
     */
    const probe: PropSpec = {
      kind,
      atM: cursor + between(rand, 0, 2.5),
      lengthM: lengthOfProp,
      heightM: kind === 'ramp' ? lengthOfProp * between(rand, 0.26, 0.33) : heightM,
    };
    const shortfall = Math.max(0, KEEP_CLEAR_M - propFootprint(probe).fromM);
    const spec: PropSpec = { ...probe, atM: probe.atM + shortfall };

    const foot = propFootprint(spec);
    if (foot.toM > usableTo) break;

    /*
     * A ramp goes on level ground or not at all.
     *
     * Mounds are rotated to lie along whatever slope they land on; a ramp cannot be, because tilting it would
     * lean its launch face - the one vertical edge in the game, and the one that is only safe *because* it is
     * vertical and pointing forwards. Levelness is also what the original design asked for: flat ground with
     * clear run-off, so a failed attempt simply rolls her back down.
     */
    if (kind === 'ramp' && !isLevel(field, spec.atM, spec.atM + spec.lengthM)) {
      cursor += 2;
      continue;
    }
    // Never standing in a patch: a log in a wallow is a log she cannot see coming.
    if (patches.some((patch) => foot.fromM < patch.toM && foot.toM > patch.fromM)) {
      cursor = Math.max(cursor + 2, patchEndNear(patches, foot.fromM));
      continue;
    }
    /*
     * And never within a car length of something she has to drive over.
     *
     * A ramp is the case that matters: she launches off it and covers the next couple of metres in the
     * air, so an item there is one she can only collect by *not* jumping. A log beside one is milder -
     * it just hides it - and the same clearance covers both.
     */
    const nearItem = items.some(
      (item) =>
        foot.fromM < item.atM + ITEM_CLEAR_OF_PROPS_M && foot.toM > item.atM - ITEM_CLEAR_OF_PROPS_M,
    );
    if (nearItem) {
      cursor += 2;
      continue;
    }

    props.push(spec);
    cursor = foot.toM;
  }

  return props;
}

/** Flat enough for a built thing to stand on. */
function isLevel(field: Heightfield, fromM: number, toM: number): boolean {
  for (let x = fromM; x <= toM; x += field.spacingM) {
    if (Math.abs(slopeAt(field, x)) > RAMP_MAX_GROUND_SLOPE) return false;
  }
  return true;
}

/** Where the patch overlapping `x` ends, so the cursor can step past it in one go. */
function patchEndNear(patches: readonly SurfaceSegment[], x: number): number {
  let end = x;
  for (const patch of patches) {
    if (x >= patch.fromM - 2 && x <= patch.toM) end = Math.max(end, patch.toM);
  }
  return end;
}

/**
 * Things with no collision: an overpass to drive under, a gate that lifts, a signpost, some bunting.
 *
 * Placed loosely, because nothing here can be in the way. The overpass is the one worth having - driving
 * *underneath* something is a different feeling from driving past it - so it is tried first.
 */
function placeDecor(
  rand: () => number,
  difficulty: number,
  lengthM: number,
  props: readonly PropSpec[],
  items: readonly ItemSpec[],
): readonly DecorSpec[] {
  const count = 1 + Math.floor(rand() * (1 + difficulty * 2));
  const kinds: DecorKind[] = ['overpass', 'gate', 'bunting', 'signpost'];
  const decor: DecorSpec[] = [];

  for (let i = 0; i < count; i++) {
    const kind = kinds[Math.min(kinds.length - 1, i === 0 ? 0 : 1 + Math.floor(rand() * 3))]!;
    const size = DECOR_SIZES[kind];
    const span = between(rand, size.minLengthM, size.maxLengthM);
    const atM = between(rand, lengthM * 0.15, Math.max(lengthM * 0.2, lengthM * 0.85 - span));

    // Not straddling a prop: a gate post inside a hay bale looks like a mistake, which it would be.
    const clashes = props.some((prop) => {
      const foot = propFootprint(prop);
      return atM < foot.toM && atM + span > foot.fromM;
    });
    const crowds = decor.some(
      (other) => atM < other.atM + other.lengthM + 2 && atM + span > other.atM - 2,
    );
    // Nor standing on the thing she is coming to fetch. A signpost planted on the ball hides the ball,
    // and an item she cannot see is an item she drives past.
    const hides = items.some((item) => atM < item.atM + 1 && atM + span > item.atM - 1);
    if (clashes || crowds || hides) continue;

    decor.push({
      kind,
      atM,
      lengthM: span,
      heightM: between(rand, size.minHeightM, size.maxHeightM),
    });
  }

  return decor.sort((a, b) => a.atM - b.atM);
}

/** Sizes in metres. An overpass has to clear the tallest vehicle with room to spare. */
const DECOR_SIZES: Readonly<
  Record<DecorKind, { minLengthM: number; maxLengthM: number; minHeightM: number; maxHeightM: number }>
> = {
  // heightM is the *clearance* under the deck, not the top of the structure.
  overpass: { minLengthM: 5, maxLengthM: 7.5, minHeightM: 2.9, maxHeightM: 3.8 },
  // A level-crossing boom, not a lamp post: it should sit at about the height of the car's roof.
  gate: { minLengthM: 2.6, maxLengthM: 3.6, minHeightM: 0.95, maxHeightM: 1.25 },
  signpost: { minLengthM: 0.8, maxLengthM: 0.8, minHeightM: 1.5, maxHeightM: 2.1 },
  bunting: { minLengthM: 4, maxLengthM: 7, minHeightM: 2.4, maxHeightM: 3.2 },
};

/**
 * Patches of something other than the default ground.
 *
 * Every kind has a rule about where it may go, and one of them is a safety rule rather than an aesthetic
 * one: **ice only on the level.** Ice on a climb is the single combination in the game that can genuinely
 * defeat her - the slope clamp guarantees a hill is climbable at normal grip, and ice takes the grip away.
 *
 * The mud-then-water pairing is kept deliberately rather than left to chance: driving through mud makes her
 * car filthy and driving through the stream a few seconds later washes it clean. That is the best
 * cause-and-effect pair in the game, and it only works if the two are close together and in that order.
 */
function placePatches(args: {
  readonly recipe: RouteRecipe;
  readonly rand: () => number;
  readonly difficulty: number;
  readonly lengthM: number;
  readonly bands: readonly BiomeBand[];
  readonly blendM: number;
  readonly field: Heightfield;
}): readonly SurfaceSegment[] {
  const { recipe, rand, difficulty, lengthM, bands, blendM, field } = args;
  const wanted = Math.round(lerp(recipe.patchesMin, recipe.patchesMax, difficulty));
  const patches: SurfaceSegment[] = [];
  if (wanted <= 0) return patches;

  const fits = (fromM: number, toM: number): boolean => {
    if (fromM < KEEP_CLEAR_M) return false;
    if (toM > lengthM - KEEP_CLEAR_M) return false;
    for (const patch of patches) {
      if (fromM < patch.toM + PATCH_GAP_M && toM > patch.fromM - PATCH_GAP_M) return false;
    }
    return true;
  };

  /** Flattest span found near a wanted position - what a puddle and an ice sheet both need. */
  const level = (fromM: number, toM: number): number => {
    let worst = 0;
    for (let x = fromM; x <= toM; x += field.spacingM) {
      worst = Math.max(worst, Math.abs(slopeAt(field, x)));
    }
    return worst;
  };

  /** How low the middle of a span sits relative to its ends: positive means a dip. */
  const dip = (fromM: number, toM: number): number => {
    const middle = heightAt(field, (fromM + toM) / 2);
    return (heightAt(field, fromM) + heightAt(field, toM)) / 2 - middle;
  };

  let attempts = 0;
  while (patches.length < wanted && attempts < wanted * 24) {
    attempts++;

    const atM = between(rand, KEEP_CLEAR_M, lengthM - KEEP_CLEAR_M);
    const local = bandAt(bands, atM, blendM);
    const options = BIOMES[local].patches;
    if (options.length === 0) continue;

    const kind = options[Math.min(options.length - 1, Math.floor(rand() * options.length))]!;
    const width = widthFor(kind, rand);
    const fromM = atM;
    const toM = atM + width;

    if (kind === 'mud') {
      /*
       * Mud and its stream are placed as one thing, or not at all.
       *
       * Driving through mud makes her car filthy and driving through the stream a few seconds later washes
       * it clean: the best cause-and-effect pair in the game, and it only works if the two are close
       * together and in that order. Placing the mud first and *hoping* the stream fit gave the pairing
       * about three times in ten - measured - because mud landing late on the route left no room. Testing
       * the whole pair before committing to either gives it nearly every time.
       */
      const gap = between(rand, 3, 7);
      const washFrom = toM + gap;
      const washTo = washFrom + widthFor('water', rand);
      if (!fits(fromM, washTo)) continue;
      patches.push({ fromM, toM, surface: 'mud' });
      patches.push({ fromM: washFrom, toM: washTo, surface: 'water' });
      continue;
    }

    if (!fits(fromM, toM)) continue;
    if (kind === 'ice' && level(fromM, toM) > ICE_MAX_SLOPE) continue;
    // A puddle on a hillside looks like spilt paint; in a hollow it looks like water.
    if (kind === 'water' && dip(fromM, toM) < 0.02) continue;

    patches.push({ fromM, toM, surface: kind });
  }

  return patches.sort((a, b) => a.fromM - b.fromM);
}

function widthFor(kind: SurfaceId, rand: () => number): number {
  switch (kind) {
    // Long enough for the dirt to build up visibly - a couple of seconds at driving speed.
    case 'mud':
      return between(rand, 6, 11);
    // Short: a puddle she splashes through, not a lake she wades.
    case 'water':
      return between(rand, 3, 5.5);
    case 'ice':
      return between(rand, 4, 8);
    case 'sand':
      return between(rand, 5, 10);
    default:
      return between(rand, 4, 8);
  }
}

/** Which biome is solidly at x - patches belong to a place, so the blend does not matter here. */
function bandAt(bands: readonly BiomeBand[], x: number, _blendM: number): BiomeId {
  for (const band of bands) {
    if (x < band.toM) return band.biome;
  }
  return bands[bands.length - 1]?.biome ?? 'meadow';
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * Math.max(0, Math.min(1, t));
}
